/**
 * PIECalibrationBadge — small chip surfacing a prediction's
 * calibration block (M5 feedback loop). Renders next to a raw ROE
 * forecast so the user sees the engine's audit-aware estimate
 * alongside the unbiased prediction.
 *
 * Three render branches:
 *   • applied=true               → "Calibrated 74% · raw 64%" with explainer tooltip
 *   • reason=insufficient_signal → "Calibration: warming up" (small, muted)
 *   • reason=no_history          → null (silent — no audit data exists)
 *
 * The tooltip reads the historical_avg_diff_pp + sample_size from the
 * payload so it can name the engine's bias direction in plain words.
 */
import React, { useState } from 'react';
import { Scale, Info } from 'lucide-react';

export default function PIECalibrationBadge({ calibration, compact = false }) {
  const [open, setOpen] = useState(false);

  if (!calibration) return null;
  if (calibration.reason === 'no_history') return null;

  if (calibration.reason === 'insufficient_signal') {
    return (
      <span
        data-testid="pie-calibration-badge-warming"
        className="pie-mono"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 11, color: 'var(--pie-text-muted)',
          background: 'var(--pie-surface)',
          padding: '3px 8px', borderRadius: 999,
          border: '1px dashed var(--pie-border)',
        }}
        title={
          `Calibration warming up — ${calibration.sample_size || 0} validated commitment${
            (calibration.sample_size || 0) === 1 ? '' : 's'
          } so far for users in this archetype. Need at least 5 to apply a tuning offset.`
        }
      >
        <Scale size={10} aria-hidden /> Calibration warming
      </span>
    );
  }

  // applied=true branch
  const off = calibration.offset_pp || 0;
  const direction = off >= 0 ? 'conservative' : 'optimistic';
  const sign = off >= 0 ? '+' : '';
  const cal = calibration.calibrated_roe_pct;
  const raw = calibration.raw_roe_pct;
  const histDiff = calibration.historical_avg_diff_pp || 0;
  const histSign = histDiff >= 0 ? '+' : '';

  return (
    <span
      data-testid="pie-calibration-badge-applied"
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid="pie-calibration-badge-applied-btn"
        onClick={() => setOpen((v) => !v)}
        className="pie-mono"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: compact ? 11 : 12,
          color: '#0E7490',
          background: 'rgba(6, 182, 212, 0.12)',
          padding: compact ? '3px 9px' : '4px 11px',
          borderRadius: 999,
          border: '1px solid rgba(6, 182, 212, 0.35)',
          fontWeight: 600,
          cursor: 'pointer',
          letterSpacing: 0.2,
        }}
        aria-label={`Calibrated forecast ${cal}% — raw ${raw}%`}
      >
        <Scale size={11} aria-hidden />
        Calibrated {cal}%
        <span style={{ color: 'var(--pie-text-muted)', fontWeight: 400 }}>
          (raw {raw}%)
        </span>
        <Info size={10} aria-hidden style={{ opacity: 0.7 }} />
      </button>
      {open && (
        <div
          role="tooltip"
          data-testid="pie-calibration-badge-tooltip"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0,
            zIndex: 50, minWidth: 280, maxWidth: 360,
            background: 'var(--pie-text)', color: 'var(--pie-on-text)',
            padding: '12px 14px', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
            fontSize: 12, lineHeight: 1.5, fontWeight: 400,
            letterSpacing: 0,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>
            Why this differs from the raw forecast
          </div>
          <div style={{ color: 'var(--pie-on-text-soft)' }}>
            Across <strong>{calibration.sample_size}</strong> validated commitments
            from users in the <em>{calibration.archetype.replace('_', ' ')}</em>{' '}
            archetype, the engine has been about{' '}
            <strong>{histSign}{histDiff}pp {direction}</strong>.
            We&apos;ve adjusted your forecast by <strong>{sign}{off}pp</strong>{' '}
            (half the historical delta, capped at ±15pp) so the number you see
            reflects what people like you have actually realised — not just
            what the engine&apos;s math projects.
          </div>
        </div>
      )}
    </span>
  );
}
